export interface ScheduleRecord {
  id: string;
  date: string;
  title: string;
  color?: string;
  memo?: string | null;
}

const JOURNAL_SCHEDULE_PREFIX = 'journal:';
export const JOURNAL_SCHEDULE_COLOR = '#E07040';

export type JournalDeviceType = 'pc' | 'mobile';
export interface JournalLayoutInfo {
  device: JournalDeviceType;
  canvasWidth: number;
  sticker?: string;
}

export const getCurrentJournalDevice = (): JournalDeviceType =>
  window.matchMedia('(pointer: coarse)').matches || window.innerWidth <= 768 ? 'mobile' : 'pc';

const createJournalScheduleMemo = (journalId: string, layout?: JournalLayoutInfo) =>
  [
    `${JOURNAL_SCHEDULE_PREFIX}${journalId}`,
    ...(layout ? [
      `device:${layout.device}`,
      `width:${Math.round(layout.canvasWidth)}`,
      ...(layout.sticker ? [`sticker:${layout.sticker}`] : []),
    ] : []),
  ].join('|');

export const parseJournalScheduleMemo = (memo?: string | null) => {
  if (!memo?.startsWith(JOURNAL_SCHEDULE_PREFIX)) return null;
  const parts = memo.split('|');
  const journalId = parts[0].slice(JOURNAL_SCHEDULE_PREFIX.length);
  const devicePart = parts.find(part => part.startsWith('device:'))?.slice(7);
  const width = Number(parts.find(part => part.startsWith('width:'))?.slice(6));
  const sticker = parts.find(part => part.startsWith('sticker:'))?.slice(8);
  return {
    journalId,
    layout: devicePart === 'pc' || devicePart === 'mobile'
      ? {
          device: devicePart,
          canvasWidth: Number.isFinite(width) && width > 0 ? width : 0,
          ...(sticker ? { sticker } : {}),
        } as JournalLayoutInfo
      : undefined,
  };
};

const API_BASE = process.env.REACT_APP_API_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const readResponseBody = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const getErrorMessage = (payload: any, fallback: string) => {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (typeof payload?.detail === 'string') return payload.detail;
  if (typeof payload?.message === 'string') return payload.message;
  return fallback;
};

const request = async (path: string, init?: RequestInit) => {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...init?.headers,
    },
  });
  const payload = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, `일정 요청에 실패했습니다. (${response.status})`));
  }

  return payload;
};

const findScheduleArray = (payload: any): any[] => {
  const candidates = [
    payload,
    payload?.data,
    payload?.schedules,
    payload?.items,
    payload?.list,
    payload?.result,
    payload?.data?.schedules,
    payload?.data?.items,
    payload?.result?.schedules,
  ];
  return candidates.find(Array.isArray) ?? [];
};

const toDateOnly = (value: unknown) => {
  const date = String(value ?? '');
  const match = date.match(/^\d{4}-\d{2}-\d{2}/);
  return match?.[0] ?? '';
};

export const normalizeSchedule = (item: any): ScheduleRecord | null => {
  if (!item || typeof item !== 'object') return null;

  const rawId = item.schedule_id ?? item.scheduleId ?? item.id ?? item._id;
  const date = toDateOnly(
    item.schedule_date ?? item.scheduleDate ?? item.date ?? item.start_date ?? item.startDate ?? item.start_at ?? item.startAt
  );
  const title = String(item.title ?? item.schedule_title ?? item.scheduleTitle ?? item.content ?? item.name ?? '').trim();

  if (rawId == null || !date || !title) return null;
  return {
    id: String(rawId),
    date,
    title,
    color: item.color ? String(item.color) : undefined,
    memo: item.memo == null ? null : String(item.memo),
  };
};

export const normalizeScheduleList = (payload: any): ScheduleRecord[] =>
  findScheduleArray(payload)
    .map(normalizeSchedule)
    .filter((schedule): schedule is ScheduleRecord => schedule !== null);

export const fetchSchedules = async (signal?: AbortSignal): Promise<ScheduleRecord[]> => {
  const payload = await request('/schedule', { signal });
  return normalizeScheduleList(payload);
};

const parseDeliveryDate = (value: any) => {
  if (!value) return 0;
  if (Array.isArray(value)) {
    return new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0).getTime();
  }
  let normalized = String(value).replace(' ', 'T');
  if (!normalized.endsWith('Z') && !normalized.includes('+')) normalized += '+09:00';
  return new Date(normalized).getTime();
};

const resolveDeliveryArrival = (mail: any, sentAt: number, now: number) => {
  if (mail.estimated_arrival_at) return parseDeliveryDate(mail.estimated_arrival_at);
  if (mail.remaining_minutes !== undefined && mail.remaining_minutes !== null && mail.remaining_minutes !== '') {
    return now + Number(mail.remaining_minutes) * 60000;
  }
  const receivedAt = mail.received_at ? parseDeliveryDate(mail.received_at) : 0;
  if (receivedAt && Math.abs(receivedAt - sentAt) >= 60000) return receivedAt;
  const fallbackMinutes = Number(mail.arrival_delay_minutes) > 0
    ? Number(mail.arrival_delay_minutes)
    : (!isNaN(Number(mail.send_type)) && Number(mail.send_type) > 0 ? Number(mail.send_type) : 8640);
  return sentAt + fallbackMinutes * 60000;
};

const formatLocalDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const findMailArray = (payload: any): any[] => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.data ?? payload?.mails ?? payload?.items ?? payload?.result ?? [];
  return Array.isArray(list) ? list : [];
};

export const fetchDeliverySchedules = async (signal?: AbortSignal): Promise<ScheduleRecord[]> => {
  const requestOptions: RequestInit = {
    headers: getAuthHeaders(),
    cache: 'no-store',
    signal,
  };
  const [sentResponse, receivedResponse] = await Promise.all([
    fetch(`${API_BASE}/mail/sent`, requestOptions).catch(() => null),
    fetch(`${API_BASE}/mail/received`, requestOptions).catch(() => null),
  ]);
  const [sentPayload, receivedPayload] = await Promise.all([
    sentResponse?.ok ? sentResponse.json() : Promise.resolve([]),
    receivedResponse?.ok ? receivedResponse.json() : Promise.resolve([]),
  ]);
  const now = Date.now();
  const candidates = [
    ...findMailArray(sentPayload).map(mail => ({ mail, direction: 'sent' as const })),
    ...findMailArray(receivedPayload).map(mail => ({ mail, direction: 'received' as const })),
  ];

  return candidates
    .map(({ mail, direction }): ScheduleRecord | null => {
      const rawId = mail.id ?? mail.mail_id ?? mail.mailId;
      const sentAt = parseDeliveryDate(mail.sent_at);
      const arrivalAt = resolveDeliveryArrival(mail, sentAt, now);
      if (rawId == null || !sentAt || !arrivalAt || arrivalAt <= now) return null;
      const counterpart = direction === 'sent'
        ? mail.receiver?.user_alias || mail.receiver?.user_name || mail.receiver_alias || mail.receiver_email || '받는 사람'
        : mail.user?.user_alias || mail.user?.user_name || mail.sender_alias || mail.sender_email || '보낸 사람';
      return {
        id: `delivery-${direction}-${rawId}`,
        date: formatLocalDate(arrivalAt),
        title: direction === 'sent'
          ? `✈️ ${counterpart}에게 보낸 편지 도착 예정`
          : `💌 ${counterpart}님의 편지 도착 예정`,
        color: '#4F9FC4',
        memo: `delivery:${rawId}:${direction}`,
      };
    })
    .filter((event): event is ScheduleRecord => event !== null)
    .filter((event, index, events) => events.findIndex(candidate => candidate.id === event.id) === index);
};

export const createSchedule = async (
  date: string,
  title: string,
  options?: { color?: string; memo?: string }
): Promise<ScheduleRecord | null> => {
  const payload = await request('/schedule', {
    method: 'POST',
    body: JSON.stringify({
      title,
      is_all_day: true,
      start_date: `${date}T00:00:00`,
      end_date: `${date}T23:59:59`,
      ...(options?.color ? { color: options.color } : {}),
      ...(options?.memo ? { memo: options.memo } : {}),
    }),
  });
  return normalizeSchedule(payload?.data ?? payload?.schedule ?? payload);
};

export const deleteSchedule = async (scheduleId: string): Promise<void> => {
  await request(`/schedule/${encodeURIComponent(scheduleId)}`, { method: 'DELETE' });
};

export const getScheduleDetail = async (scheduleId: string): Promise<ScheduleRecord | null> => {
  const payload = await request(`/schedule/${encodeURIComponent(scheduleId)}`);
  return normalizeSchedule(payload?.data ?? payload?.schedule ?? payload);
};

export const findJournalSchedule = async (journalId: string): Promise<ScheduleRecord | null> => {
  const schedules = await fetchSchedules();
  const journalTitleSchedules = schedules.filter(schedule =>
    schedule.title.startsWith('📔')
  );
  const details = await Promise.all(journalTitleSchedules.map(schedule => getScheduleDetail(schedule.id)));
  return details.find(schedule => parseJournalScheduleMemo(schedule?.memo)?.journalId === journalId) ?? null;
};

export const getJournalLayoutInfo = async (journalId: string): Promise<JournalLayoutInfo | undefined> => {
  const linked = await findJournalSchedule(journalId);
  return parseJournalScheduleMemo(linked?.memo)?.layout;
};

export const getJournalLayoutMap = async (journalIds: string[]) => {
  const wanted = new Set(journalIds);
  const schedules = (await fetchSchedules()).filter(schedule => schedule.title.startsWith('📔'));
  const details = await Promise.all(schedules.map(schedule => getScheduleDetail(schedule.id)));
  const result: Record<string, JournalLayoutInfo> = {};
  details.forEach(schedule => {
    const parsed = parseJournalScheduleMemo(schedule?.memo);
    if (parsed?.layout && wanted.has(parsed.journalId)) result[parsed.journalId] = parsed.layout;
  });
  return result;
};

export const syncJournalSchedule = (
  journalId: string,
  date: string,
  journalTitle: string,
  layout?: JournalLayoutInfo
) => {
  return syncJournalScheduleRequest(journalId, date, journalTitle, layout);
};

const syncJournalScheduleRequest = async (
  journalId: string,
  date: string,
  journalTitle: string,
  layout?: JournalLayoutInfo
) => {
  const linked = await findJournalSchedule(journalId);
  const memo = createJournalScheduleMemo(journalId, layout);
  const payload = {
    title: `📔 ${journalTitle}`,
    color: JOURNAL_SCHEDULE_COLOR,
    is_all_day: true,
    start_date: `${date}T00:00:00`,
    end_date: `${date}T23:59:59`,
    memo,
  };

  if (linked) {
    await request(`/schedule/${encodeURIComponent(linked.id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  } else {
    await createSchedule(date, `📔 ${journalTitle}`, {
      color: JOURNAL_SCHEDULE_COLOR,
      memo,
    });
  }
  window.dispatchEvent(new Event('neurit-calendar-refresh'));
};

export const deleteJournalSchedule = async (journalId: string) => {
  const linked = await findJournalSchedule(journalId);
  if (linked) await deleteSchedule(linked.id);
  window.dispatchEvent(new Event('neurit-calendar-refresh'));
};
